import React, { useState, useEffect, useCallback } from 'react';
// import PropTypes from 'prop-types';
import { useLoginRequired } from 'features/auth/redux/hooks';
import Dialog from '@mui/material/Dialog';
import { SignIn, SignUp, ConfirmSignUp, AddPhone, Otp, ForgotPassword } from '.';
import SetupAuthenticator from './SetupAuthenticator';
import { makeStyles } from '@mui/styles';
import Auth from '@aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from './redux/logout';
import { useUpdateUserSession } from './redux/updateUserSession';
import { login } from './redux/login';
import TroubleSigningIn from './TroubleSigningIn';
import Logger from 'utils/logger';
import { CognitoRefreshToken } from 'amazon-cognito-identity-js';
import { selectIsAuthenticated, selectQuery } from 'common/selectors';
import { ssoAuthService } from './ssoAuthService';
import * as actions from 'features/case/redux/actions';
import history from 'common/history';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SESSION_EXPIRED_RETURN_TO_KEY, SESSION_EXPIRED_STORAGE_KEY } from './constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '1rem',
  },
}));

const FLOW = {
  signUp: 'signUp',
  signIn: 'signIn',
  otp: 'otp',
  authenticated: 'authenticated',
  confirm: 'confirm',
  addPhone: 'addPhone',
  forgotPassword: 'forgotPassword',
  troubleSigningIn: 'troubleSigningIn',
  setupAuthenticator: 'setupAuthenticator',
};

const getFlow = (flow, setFlow, verifyAuth) => {
  switch (flow.step) {
    case FLOW.signUp:
      return (
        <SignUp
          onSignInClick={() => setFlow({ step: FLOW.signIn })}
          onSignUpCompleted={(email) => setFlow({ step: FLOW.confirm, data: { email } })}
        />
      );
    case FLOW.otp:
      return (
        <Otp
          onSignInCompleted={() => {
            setFlow({});
            verifyAuth();
          }}
          password={flow.data.password}
          session={flow.session}
          onSignInClick={() => setFlow({ step: FLOW.signIn })}
          onMfa={(user, password) => setFlow({ step: FLOW.otp, session: user, data: { password } })}
        />
      );
    case FLOW.authenticated:
      return null;
    case FLOW.confirm:
      return (
        <ConfirmSignUp
          email={flow.data.email}
          resendCode={flow.data.resendCode}
          confirmSignUpCompleted={() => setFlow({ step: FLOW.signIn })}
          onSignInClick={() => setFlow({ step: FLOW.signIn })}
          onResendVerificationEmailClicked={(email) =>
            setFlow({ step: FLOW.confirm, data: { email } })
          }
        />
      );
    case FLOW.addPhone:
      return (
        <AddPhone
          onSavePhoneCompleted={() => {
            setFlow({});
            verifyAuth();
          }}
          user={flow.session}
        />
      );
    case FLOW.setupAuthenticator:
      return (
        <SetupAuthenticator
          onFinished={() => {
            setFlow({});
            verifyAuth();
          }}
        />
      );
    case FLOW.forgotPassword:
      return (
        <ForgotPassword
          email={flow.data.email}
          onForgotPasswordCompleted={() => setFlow({ step: FLOW.signIn })}
          onSignInClick={() => setFlow({ step: FLOW.signIn })}
          onForgotPasswordClicked={(email) =>
            setFlow({ step: FLOW.forgotPassword, data: { email } })
          }
        />
      );
    case FLOW.troubleSigningIn:
      return (
        <TroubleSigningIn
          onSignupClick={() => setFlow({ step: FLOW.signUp })}
          onSignInClick={() => setFlow({ step: FLOW.signIn })}
          onResendVerificationEmailClicked={(email) =>
            setFlow({ step: FLOW.confirm, data: { email } })
          }
          onForgotPasswordClicked={(email) =>
            setFlow({ step: FLOW.forgotPassword, data: { email } })
          }
        />
      );
    case FLOW.signIn:
      return (
        <SignIn
          onTroubleSigningIn={() => setFlow({ step: FLOW.troubleSigningIn })}
          onSignupClick={() => setFlow({ step: FLOW.signUp })}
          onMfa={(user, password) => setFlow({ step: FLOW.otp, session: user, data: { password } })}
          onRequirePhoneNumber={(user) => setFlow({ step: FLOW.addPhone, session: user })}
          onSignInCompleted={() => {
            setFlow({});
            verifyAuth();
          }}
          onConfirmUser={(email, resendCode) =>
            setFlow({ step: FLOW.confirm, data: { email, resendCode } })
          }
        />
      );
    default:
      return null;
  }
};

export default function Cognito({ children, forceLogin, cognitoSettings }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [flow, setFlow] = useState({});
  const [isVerifyAuth, setIsVerifyAuth] = useState(false);

  const { loginRequired, showScreen, removeLoginRequired } = useLoginRequired();
  const { userSession, updateUserSession } = useUpdateUserSession();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const queryParams = useSelector(selectQuery);

  const restoreSessionExpiredLocation = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const { sessionStorage, location } = window;
      if (!sessionStorage) return;
      const redirectPath = sessionStorage.getItem(SESSION_EXPIRED_RETURN_TO_KEY);
      sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
      if (!redirectPath) return;
      sessionStorage.removeItem(SESSION_EXPIRED_RETURN_TO_KEY);

      const { href, pathname, search, hash } = location;
      const currentLocation = `${pathname}${search}${hash}`;
      const isAbsoluteTarget = /^https?:\/\//i.test(redirectPath);

      if (isAbsoluteTarget) {
        if (redirectPath === href) location.reload();
        else location.assign(redirectPath);
        return;
      }

      const normalizedTarget = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
      if (normalizedTarget === currentLocation) {
        location.reload();
      } else {
        location.replace(normalizedTarget);
      }
    } catch (e) {
      Logger.WARN && Logger.WARN('Unable to restore session expired return path', e);
    }
  }, []);

  const isSessionExpiredError = useCallback((err) => {
    if (!err) return false;
    return err.code === 'NotAuthorizedException';
  }, []);

  const acceptTeamInvite = useCallback(() => {
    const { email, caseId, teamId, inviteId, countryCode } = queryParams || {};
    const hasInviteParams = email && caseId && teamId && inviteId;
    const isInviteFlow = showScreen === 'signin' || showScreen === 'signup';
    if (!hasInviteParams || !isInviteFlow) return;

    const basePath = (() => {
      try {
        const { pathname } = history.location || {};
        const cleaned = (pathname || '').replace(/\/sign.*/, '') || '/';
        return cleaned;
      } catch (e) {
        return '/';
      }
    })();

    dispatch(
      actions.acceptTeamInvite({
        emailId: email,
        caseId,
        teamId,
        inviteId,
        countryCode,
      }),
    )
      .catch((err) => {
        Logger.ERROR(err);
        return err;
      })
      .finally(() => {
        history.replace(basePath);
      });
  }, [dispatch, history, queryParams, showScreen]);

  const verifyAuth = useCallback(async () => {
    // First, check if there's a valid cookie session (covers SSO and cookie-based auth)
    const unifiedCookieEnabled = process.env.REACT_APP_UNIFIED_COOKIE_ENABLED === 'true';
    if (unifiedCookieEnabled) {
      try {
        const sessionInfo = await ssoAuthService.validateSession();
        if (sessionInfo.authenticated) {
          Logger.INFO('Authenticated via session cookie, authMethod=' + sessionInfo.authMethod);
          // Create a synthetic user object for Redux state
          const ssoUser = {
            username: sessionInfo.userId,
            attributes: {
              email: sessionInfo.email,
              sub: sessionInfo.identityId,
            },
            signInUserSession: {
              accessToken: { jwtToken: 'cookie-session' },
              idToken: { jwtToken: 'cookie-session' },
            },
            authMethod: sessionInfo.authMethod,
          };
          dispatch(login(ssoUser, showScreen));
          acceptTeamInvite();
          setFlow({});
          restoreSessionExpiredLocation();
          removeLoginRequired();
          setIsVerifyAuth(false);
          return;
        }
      } catch (e) {
        Logger.ERROR('Cookie session validation failed:', e);
      }
    }

    // Fall back to Cognito auth check
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        // Exchange Cognito token for a unified session cookie (best-effort).
        // If this fails, Bearer token is still sent on every request (see api.js),
        // so the user remains authenticated via the Cognito fallback path.
        if (process.env.REACT_APP_UNIFIED_COOKIE_ENABLED === 'true') {
          try {
            const session = await Auth.currentSession();
            const idToken = session.getIdToken().getJwtToken();
            await ssoAuthService.exchangeSession(idToken);
          } catch (e) {
            Logger.WARN && Logger.WARN('Session exchange failed (Bearer fallback active):', e);
          }
        }

        dispatch(login(JSON.parse(JSON.stringify(user)), showScreen));
        if (!user.attributes.phone_number) setFlow({ step: FLOW.addPhone, session: user });
        else {
          acceptTeamInvite();
          setFlow({});
        }
        restoreSessionExpiredLocation();
        removeLoginRequired();
        setIsVerifyAuth(false);
      })
      .catch((err) => {
        if (isSessionExpiredError(err) && isAuthenticated) dispatch(logout('sessionExpired'));
        if (forceLogin || loginRequired) {
          if (showScreen === 'signin') setFlow({ step: FLOW.signIn });
          else if (showScreen === 'signup') setFlow({ step: FLOW.signUp });
          else setFlow({ step: FLOW.signIn });
        }
        Logger.ERROR(err);
        setIsVerifyAuth(false);
      });
  }, [
    forceLogin,
    removeLoginRequired,
    dispatch,
    loginRequired,
    acceptTeamInvite,
    showScreen,
    restoreSessionExpiredLocation,
    isSessionExpiredError,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Skip Cognito token refresh for SSO/cookie-based sessions
    const user = userSession && userSession.accessToken;
    if (!user || !userSession.refreshToken || userSession.accessToken.jwtToken === 'cookie-session') return;

    // Use a safe default if env var is missing/invalid
    const configuredInterval = parseInt(process.env.REACT_APP_COGNITO_REFRESH_TOKEN_CHECK_INTERVAL);
    const interval = isNaN(configuredInterval) ? 60 : configuredInterval; // seconds
    if (!isNaN(interval)) {
      const intervalId = setInterval(() => {
        const accessToken = userSession.accessToken;
        const refreshToken = new CognitoRefreshToken({
          RefreshToken: userSession.refreshToken.token,
        });

        const currentTimeSeconds = Math.round(+new Date() / 1000);
        if (currentTimeSeconds + interval * 2 > accessToken.payload.exp)
          setTimeout(
            () => {
              Auth.currentAuthenticatedUser()
                .then((res) => {
                  res.refreshSession(refreshToken, (err, data) => {
                    if (err) {
                      Logger.ERROR(err);
                      if (err.code === 'NotAuthorizedException') dispatch(logout('sessionExpired'));
                    } else {
                      Logger.INFO('Token refreshed ' + data);
                      updateUserSession(data);
                    }
                  });
                })
                .catch((err) => {
                  // If we can't even get the current user and it's a NotAuthorizedException, treat as expired
                  Logger.ERROR(err);
                  if (err && err.code === 'NotAuthorizedException')
                    dispatch(logout('sessionExpired'));
                });
            },
            Math.floor(Math.random() * interval),
          );
      }, interval * 1000);
      return () => intervalId && clearInterval(intervalId);
    }
  }, [dispatch, isAuthenticated, userSession, updateUserSession, isSessionExpiredError]);

  const onMount = () => {
    const authConfig = {
      aws_project_region: 'eu-west-1',
      aws_cognito_region: 'eu-west-1',
      // aws_cognito_identity_pool_id: 'eu-west-1:cd33f2ec-6af5-4d75-8088-3e623f829f28',
      // aws_user_pools_id: 'eu-west-1_FDWlNQuxP',
      // aws_user_pools_web_client_id: '6438d76sjhrua3ltbu9q09m760',
      oauth: {},
      ...(process.env.REACT_APP_COGNITO_MIGRATION_FLOW === 'true' && {
        authenticationFlowType: 'USER_PASSWORD_AUTH',
      }),
      ...cognitoSettings,
    };
    // Configure Amplify v4 Auth (existing codebase APIs use this)
    Auth.configure(authConfig);
    setIsVerifyAuth(true);
    verifyAuth();
  };

  useEffect(onMount, []);

  useEffect(() => {
    loginRequired && !isVerifyAuth && verifyAuth();
  }, [loginRequired, isVerifyAuth, verifyAuth]);

  // Watch enforcement flags from /users-me and lock UI inside this dialog when needed
  const mustUseAuthenticatorApp = useSelector((s) => s.auth.mustUseAuthenticatorApp);
  const hasAuthenticatorApp = useSelector((s) => s.auth.hasAuthenticatorApp);
  const enforceTOTPSetup = mustUseAuthenticatorApp && !hasAuthenticatorApp;

  useEffect(() => {
    if (!isAuthenticated) return;
    if (enforceTOTPSetup) {
      if (flow.step !== FLOW.setupAuthenticator) setFlow({ step: FLOW.setupAuthenticator });
    } else {
      if (flow.step === FLOW.setupAuthenticator) setFlow({});
    }
  }, [enforceTOTPSetup, isAuthenticated, flow.step]);

  return (
    <div className="auth-cognito">
      {children}
      {flow.step && (
        <Dialog
          open={
            flow.step === FLOW.addPhone ||
            flow.step === FLOW.setupAuthenticator ||
            (!isAuthenticated && (forceLogin || loginRequired))
          }
          disableEscapeKeyDown={forceLogin || flow.step === FLOW.setupAuthenticator}
          onClose={(e, reason) => {
            if (flow.step === FLOW.setupAuthenticator) return; // locked while enforcing
            if (reason !== 'backdropClick') !forceLogin && removeLoginRequired();
          }}
          classes={{
            paper: classes.paper,
            margin: 0,
          }}
        >
          {flow.step !== FLOW.setupAuthenticator && (
            <IconButton
              style={{ marginLeft: 'auto' }}
              onClick={() => !forceLogin && removeLoginRequired()}
            >
              <CloseIcon />
            </IconButton>
          )}
          {getFlow(flow, setFlow, verifyAuth)}
        </Dialog>
      )}
    </div>
  );
}

Cognito.propTypes = {};
Cognito.defaultProps = {};
